import { Component, EventEmitter, Output, Input, ChangeDetectionStrategy, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LeadManagementService } from '../../../../core/services/lead-management/lead-management.service';
import { LeadRequest, LeadType } from '../../../../core/models/lead.model';
import { CalendarModule } from 'primeng/calendar';

import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-book-demo-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CalendarModule],
  templateUrl: './book-demo-modal.component.html',
  styleUrls: ['./book-demo-modal.component.scss']
})
export class BookDemoModalComponent implements OnInit, OnDestroy {
  @Input() initialOption: 'demo' | 'quote' = 'demo';
  @Output() close = new EventEmitter<void>();

  demoForm: FormGroup;
  selectedOption: 'demo' | 'quote' = 'demo';
  isSubmitting = false;

  errorMessage: string | null = null;
  minDate: string;
  minTimeConstraint: Date | null = null;
  private dateSub: any;

  constructor(
    private fb: FormBuilder,
    private leadService: LeadManagementService,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService
  ) {
    const today = new Date();
    this.minDate = today.toISOString().split('T')[0];

    this.demoForm = this.fb.group({
      fullName: ['', [
        Validators.required, 
        Validators.minLength(2), 
        Validators.maxLength(50), 
        Validators.pattern(/^[a-zA-Z\s\-'.]+$/)
      ]],
      workEmail: ['', [
        Validators.required, 
        Validators.email, 
        Validators.maxLength(100),
        Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,10}$')
      ]],
      phoneNumber: ['', [
        Validators.required, 
        Validators.minLength(10),
        Validators.maxLength(10),
        Validators.pattern('^[0-9]{10}$')
      ]],
      companyName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100)
      ]],
      companySize: ['', Validators.required],
      hiringVolume: ['', Validators.required],
      additionalInfo: ['', [Validators.maxLength(500)]],
      demoDate: [''],
      demoTime: ['']
    });
  }

  ngOnInit() {
    this.selectedOption = this.initialOption;
    this.updateValidators();

    this.dateSub = this.demoForm.get('demoDate')?.valueChanges.subscribe(val => {
      if (!val) {
        this.minTimeConstraint = null;
        return;
      }

      const selectedDate = new Date(val);
      const today = new Date();

      if (
        selectedDate.getFullYear() === today.getFullYear() &&
        selectedDate.getMonth() === today.getMonth() &&
        selectedDate.getDate() === today.getDate()
      ) {
        this.minTimeConstraint = new Date();
      } else {
        this.minTimeConstraint = null;
      }

      const currentTime = this.demoForm.get('demoTime')?.value;
      if (this.minTimeConstraint && currentTime) {
        const ctDate = new Date(currentTime);
        // We only care about time portion for p-calendar timeOnly
        const ctMinutes = ctDate.getHours() * 60 + ctDate.getMinutes();
        const minMinutes = this.minTimeConstraint.getHours() * 60 + this.minTimeConstraint.getMinutes();

        if (ctMinutes < minMinutes) {
          this.demoForm.get('demoTime')?.setValue(this.minTimeConstraint);
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.dateSub) {
      this.dateSub.unsubscribe();
    }
  }

  setOption(option: 'demo' | 'quote') {
    this.selectedOption = option;
    this.updateValidators();
  }

  private updateValidators() {
    const dateControl = this.demoForm.get('demoDate');
    const timeControl = this.demoForm.get('demoTime');

    if (this.selectedOption === 'demo') {
      dateControl?.setValidators([Validators.required]);
      timeControl?.setValidators([Validators.required]);
    } else {
      dateControl?.clearValidators();
      timeControl?.clearValidators();
    }
    dateControl?.updateValueAndValidity();
    timeControl?.updateValueAndValidity();
  }

  onClose() {
    this.close.emit();
  }

  onSubmit() {
    if (this.demoForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = null;
      this.cdr.markForCheck();

      const formValue = this.demoForm.value;

      if (this.selectedOption === 'demo' && formValue.demoDate && formValue.demoTime) {
        const selectedDate = new Date(formValue.demoDate);
        const today = new Date();
        if (
          selectedDate.getFullYear() === today.getFullYear() &&
          selectedDate.getMonth() === today.getMonth() &&
          selectedDate.getDate() === today.getDate()
        ) {
          const selectedTime = formValue.demoTime instanceof Date ? formValue.demoTime : new Date(formValue.demoTime);
          const ctMinutes = selectedTime.getHours() * 60 + selectedTime.getMinutes();
          const currentMinutes = today.getHours() * 60 + today.getMinutes();
          
          if (ctMinutes < currentMinutes) {
            this.errorMessage = "The selected time has already passed. Please choose a future time.";
            this.isSubmitting = false;
            this.cdr.markForCheck();
            return;
          }
        }
      }

      let formattedTime = null;
      if (this.selectedOption === 'demo' && formValue.demoTime) {
        const d = formValue.demoTime instanceof Date ? formValue.demoTime : new Date(formValue.demoTime);

        if (!isNaN(d.getTime())) {
          let h = d.getHours();
          const m = d.getMinutes().toString().padStart(2, '0');
          const s = d.getSeconds().toString().padStart(2, '0');
          const ampm = h >= 12 ? 'PM' : 'AM';
          h = h % 12 || 12;
          const hStr = h.toString().padStart(2, '0');
          formattedTime = `${hStr}:${m}:${s} ${ampm}`;
        }
      }

      const requestPayload: LeadRequest = {
        lead_type: this.selectedOption === 'demo' ? LeadType.BOOK_DEMO : LeadType.GET_QUOTE,
        full_name: formValue.fullName,
        work_email: formValue.workEmail,
        phone_number: formValue.phoneNumber,
        company_name: formValue.companyName,
        company_size: formValue.companySize,
        hiring_volume: formValue.hiringVolume,
        additional_information: formValue.additionalInfo || null,
        preferred_demo_date: this.selectedOption === 'demo' ? formValue.demoDate : null,
        preferred_demo_time: formattedTime,
      };

      this.leadService.submitLead(requestPayload).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.toastr.success("Request submitted successfully! Our team will reach out to you soon.");
          this.cdr.markForCheck();

          // Auto close after 3 seconds
          setTimeout(() => {
            this.close.emit();
          }, 3000);
        },
        error: (error) => {
          this.isSubmitting = false;

          if (error.status === 409) {
            this.errorMessage = error.error?.message || error.error?.detail || "We already received a request with this email or phone number. Our team will contact you shortly!";
          } else if (error.status === 422) {
            this.errorMessage = "Please check your input fields. Ensure the date/time is valid.";
          } else {
            this.errorMessage = "An unexpected error occurred. Please try again.";
          }

          this.cdr.markForCheck();
        }
      });
    } else {
      this.demoForm.markAllAsTouched();
    }
  }
  allowNumbersOnly(event: any): void {
    const input = event.target.value;
    const numericValue = input.replace(/[^0-9]/g, '');
    if (input !== numericValue) {
      this.demoForm.get('phoneNumber')?.setValue(numericValue, { emitEvent: false });
      event.target.value = numericValue;
    }
  }
}
